import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { rulesApi, groupsApi } from '../../services/api';
import type { GroupRule, CatalogRule, CatalogRuleArg } from '../../types';

interface Props {
  rule: GroupRule;
  groupId: number;
  groupName: string;
  projectName: string;
  onClose: () => void;
  onSaved: () => void;
}

const EditRuleArgsModal: React.FC<Props> = ({ rule, groupId, groupName, projectName, onClose, onSaved }) => {
  const [catalogArgs, setCatalogArgs] = useState<CatalogRuleArg[]>([]);
  const [argsLoading, setArgsLoading] = useState(true);
  const [args, setArgs] = useState<Record<string, any>>({ ...rule.args });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      setArgsLoading(true);
      try {
        const resp = await rulesApi.list({ search: rule.rule, items_per_page: 50 });
        const payload = resp.data.payload;
        const rules: CatalogRule[] = payload?.rules || payload || [];
        const match = rules.find((r) => r.rule === rule.rule);
        setCatalogArgs(match?.args || []);
      } catch {
        setCatalogArgs([]);
      } finally {
        setArgsLoading(false);
      }
    })();
  }, [rule.rule]);

  const updateArg = (name: string, value: any) => {
    setArgs((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await groupsApi.editRule(rule.id, {
        project_name: projectName,
        group_name: groupName,
        group_id: groupId,
        new_rule: {
          rule: rule.rule,
          args,
          description: rule.description,
          package: rule.package,
        },
      });
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save rule args.');
    } finally {
      setSaving(false);
    }
  };

  const renderField = (argDef: CatalogRuleArg) => {
    const value = args[argDef.name] ?? argDef.default ?? '';
    const isMandatory = argDef.mandatory === 'yes';
    const t = argDef.type?.toLowerCase() || 'str';

    if (t === 'custom_dropdown' && argDef.value_list?.length) {
      return (
        <select
          value={String(value)}
          onChange={(e) => updateArg(argDef.name, e.target.value)}
          className="w-full px-3 py-2 rounded-lg bg-cargo-panel border border-cargo-border text-cargo-text text-sm
                     focus:border-cargo-accent outline-none cursor-pointer"
        >
          <option value="">Select...</option>
          {argDef.value_list.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
      );
    }

    if (t === 'bool') {
      return (
        <select
          value={String(value)}
          onChange={(e) => updateArg(argDef.name, e.target.value === 'true')}
          className="w-full px-3 py-2 rounded-lg bg-cargo-panel border border-cargo-border text-cargo-text text-sm
                     focus:border-cargo-accent outline-none cursor-pointer"
        >
          <option value="true">True</option>
          <option value="false">False</option>
        </select>
      );
    }

    if (t === 'int' || t === 'float') {
      return (
        <input
          type="number"
          value={value}
          step={t === 'float' ? 'any' : '1'}
          onChange={(e) => updateArg(argDef.name, t === 'int' ? parseInt(e.target.value) || '' : parseFloat(e.target.value) || '')}
          className="w-full px-3 py-2 rounded-lg bg-cargo-panel border border-cargo-border text-cargo-text text-sm
                     focus:border-cargo-accent focus:ring-1 focus:ring-cargo-accent outline-none"
        />
      );
    }

    if (t === 'list' || t === 'set') {
      return (
        <textarea
          value={typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
          onChange={(e) => {
            try { updateArg(argDef.name, JSON.parse(e.target.value)); } catch { updateArg(argDef.name, e.target.value); }
          }}
          rows={3}
          placeholder="JSON array, e.g. [1, 2, 3]"
          className="w-full px-3 py-2 rounded-lg bg-cargo-panel border border-cargo-border text-cargo-text text-sm font-mono
                     placeholder-cargo-muted/50 focus:border-cargo-accent focus:ring-1 focus:ring-cargo-accent outline-none resize-none"
        />
      );
    }

    // Default: string / str
    return (
      <input
        type="text"
        value={String(value)}
        onChange={(e) => updateArg(argDef.name, e.target.value)}
        placeholder={isMandatory ? 'Required' : 'Optional'}
        className="w-full px-3 py-2 rounded-lg bg-cargo-panel border border-cargo-border text-cargo-text text-sm
                   placeholder-cargo-muted/50 focus:border-cargo-accent focus:ring-1 focus:ring-cargo-accent outline-none"
      />
    );
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-cargo-surface border border-cargo-border rounded-xl w-full max-w-lg max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-cargo-border">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-cargo-text">Edit Rule Args</h2>
            <p className="text-xs text-cargo-muted font-mono mt-0.5 truncate">{rule.rule}</p>
          </div>
          <button onClick={onClose} className="ml-4 text-cargo-muted hover:text-cargo-text flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {argsLoading ? (
            <div className="py-12 text-center">
              <Loader2 className="w-5 h-5 animate-spin text-cargo-accent mx-auto" />
              <p className="text-sm text-cargo-muted mt-2">Loading arg definitions...</p>
            </div>
          ) : catalogArgs.length > 0 ? (
            catalogArgs.map((argDef) => (
              <div key={argDef.name}>
                <label className="block text-xs font-medium text-cargo-muted uppercase tracking-wider mb-1.5">
                  {argDef.name}
                  {argDef.mandatory === 'yes' && <span className="text-red-400 ml-0.5">*</span>}
                </label>
                {argDef.description && (
                  <p className="text-[11px] text-cargo-muted/70 mb-1.5">{argDef.description}</p>
                )}
                {renderField(argDef)}
              </div>
            ))
          ) : (
            // Fallback: show raw JSON editor for all args
            Object.entries(args).map(([key, val]) => (
              <div key={key}>
                <label className="block text-xs font-medium text-cargo-muted uppercase tracking-wider mb-1.5">
                  {key}
                </label>
                <input
                  type="text"
                  value={typeof val === 'object' ? JSON.stringify(val) : String(val ?? '')}
                  onChange={(e) => updateArg(key, e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-cargo-panel border border-cargo-border text-cargo-text text-sm
                             focus:border-cargo-accent focus:ring-1 focus:ring-cargo-accent outline-none"
                />
              </div>
            ))
          )}

          {error && <p className="text-xs text-red-400">{error}</p>}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-cargo-border">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg text-cargo-muted hover:text-cargo-text hover:bg-cargo-panel transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-cargo-accent text-white hover:bg-cargo-accent/90
                       disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save Args
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditRuleArgsModal;
